/**
 * /virtual-office/$service — depth page per virtual-office service line.
 */
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { absoluteAsset, absoluteUrl } from "@/config/sites";
import { canonicalAndHreflang } from "@/lib/hreflang";
import { VO_SERVICE_BY_SLUG, VO_SERVICES } from "@/data/virtual-office/axes";
import { VO_QA, VO_REVIEWED_TH } from "@/data/virtual-office/questions";
import { AnswerFirst } from "@/components/AnswerFirst";
import { FirmCta } from "@/components/firm/FirmCta";
import { QaHubCta } from "@/components/QaHubCta";
import teamAsset from "@/assets/team-group.png.asset.json";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  legalServiceSchema,
  howToSchema,
  faqPageSchema,
} from "@/lib/schema";

export const Route = createFileRoute("/virtual-office/$service")({
  loader: ({ params }) => {
    const s = VO_SERVICE_BY_SLUG[params.service];
    if (!s) throw notFound();
    return { slug: s.slug };
  },
  head: ({ loaderData }) => {
    const s = loaderData ? VO_SERVICE_BY_SLUG[loaderData.slug] : undefined;
    if (!s) {
      return { meta: [{ title: "ไม่พบหน้าที่ต้องการ" }, { name: "robots", content: "noindex" }] };
    }
    const path = `/virtual-office/${s.slug}`;
    const ogImage = absoluteAsset(teamAsset.url);
    const faqs = [
      { question: s.cautions[0]!.title, answer: s.cautions[0]!.body },
      ...s.cautions.slice(1).map((c) => ({ question: c.title, answer: c.body })),
    ];
    return {
      meta: [
        { title: ctrTitle(s.seoTitle) },
        { name: "description", content: ctrDesc(s.seoDesc) },
        { property: "og:title", content: ctrTitle(s.seoTitle) },
        { property: "og:description", content: ctrDesc(s.seoDesc) },
        { property: "og:url", content: absoluteUrl(path, null, "th") },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { property: "og:image:alt", content: "ทีมงาน Thai Notary Law & Service" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: ogImage },
      ],
      links: [
        { rel: "canonical", href: absoluteUrl(path, null, "th") },
        ...canonicalAndHreflang(path, null, "th").filter((l) => l.rel === "alternate"),
      ],
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: path, title: s.seoTitle, description: s.seoDesc }),
            breadcrumbListSchema([
              { name: "หน้าแรก", url: absoluteUrl("/", null, "th") },
              { name: "ที่ตั้งสำนักงานจดทะเบียนบริษัทและจด VAT", url: absoluteUrl("/virtual-office", null, "th") },
              { name: s.nameTh, url: absoluteUrl(path, null, "th") },
            ]),
            legalServiceSchema({
              name: s.nameTh,
              description: s.tagline,
              url: path,
              areaServed: "TH",
              serviceType: s.nameEn,
            }),
            howToSchema({
              name: `ขั้นตอน${s.nameTh}`,
              description: s.seoDesc,
              steps: s.steps.map((t, i) => ({ name: `ขั้นที่ ${i + 1}`, text: t })),
            }),
            faqPageSchema(faqs),
          ]),
        ),
      ],
    };
  },
  component: VoServicePage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24">
      <h1 className="text-2xl font-bold">ไม่พบบริการที่ต้องการ</h1>
      <p className="mt-3 text-muted-foreground">
        กลับไปที่{" "}
        <Link to="/virtual-office" className="text-primary hover:underline">
          หน้ารวมบริการที่ตั้งสำนักงานและการจด VAT
        </Link>
      </p>
    </div>
  ),
});

function VoServicePage() {
  const { slug } = Route.useLoaderData();
  const s = VO_SERVICE_BY_SLUG[slug]!;
  const related = s.related.map((r) => VO_SERVICE_BY_SLUG[r]).filter(Boolean);
  const scoped = VO_QA.filter((q) => q.topicSlug && s.slug.includes(q.topicSlug.split("-")[0]!));
  const qa = scoped.length >= 12 ? scoped : VO_QA;

  return (
    <>
      <header
        className="border-b border-border/60 text-[var(--primary-foreground)]"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div className="mx-auto max-w-6xl px-6 py-12">
          <nav aria-label="เส้นทางหน้า" className="text-xs text-[var(--primary-foreground)]/70">
            <Link to="/" className="hover:underline">
              หน้าแรก
            </Link>{" "}
            <span aria-hidden>/</span>{" "}
            <Link to="/virtual-office" className="hover:underline">
              ที่ตั้งสำนักงานและการจด VAT
            </Link>{" "}
            <span aria-hidden>/</span> {s.nameTh}
          </nav>
          <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight md:text-4xl">{s.nameTh}</h1>
          <p className="mt-4 max-w-3xl leading-relaxed text-[var(--primary-foreground)]/85">{s.tagline}</p>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-12">
        <AnswerFirst>{s.answer}</AnswerFirst>
        <p className="mt-4 text-xs text-muted-foreground">
          ข้อมูลทบทวน ณ {VO_REVIEWED_TH} · ควรยืนยันกับกรมพัฒนาธุรกิจการค้าและสำนักงานสรรพากรพื้นที่ก่อนดำเนินการ ·
          เว็บไซต์ไม่แสดงค่าบริการ ขอใบเสนอราคาเฉพาะเรื่องได้ทางโทรศัพท์ LINE หรืออีเมล
        </p>

        <section className="py-10">
          <h2 className="text-2xl font-bold text-foreground">เหมาะกับใคร</h2>
          <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
            {s.audience.map((x) => (
              <li key={x} className="flex gap-2">
                <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="border-t border-border/60 py-10">
          <h2 className="text-2xl font-bold text-foreground">ขอบเขตงานที่เราทำ</h2>
          <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
            {s.scope.map((x) => (
              <li key={x} className="flex gap-2">
                <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="border-t border-border/60 py-10">
          <h2 className="text-2xl font-bold text-foreground">เอกสารที่ต้องเตรียม</h2>
          <ul className="mt-4 grid gap-2 text-sm leading-relaxed text-muted-foreground sm:grid-cols-2">
            {s.docs.map((x) => (
              <li key={x} className="flex gap-2">
                <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">
            หากเอกสารบางฉบับยังไม่มี ส่งเท่าที่มีให้ทีมงานตรวจก่อนได้ ทีมงานจะแจ้งวิธีขอเอกสารจากหน่วยงานที่เกี่ยวข้อง
          </p>
        </section>

        <section className="border-t border-border/60 py-10">
          <h2 className="text-2xl font-bold text-foreground">ขั้นตอนการทำงาน</h2>
          <ol className="mt-4 space-y-3">
            {s.steps.map((x, i) => (
              <li key={x} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--primary)] text-xs font-bold text-[var(--gold)]">
                  {i + 1}
                </span>
                <span>{x}</span>
              </li>
            ))}
          </ol>
        </section>

        <section className="border-t border-border/60 py-10">
          <h2 className="text-2xl font-bold text-foreground">ข้อควรระวังที่พบบ่อย</h2>
          <dl className="mt-4 space-y-5">
            {s.cautions.map((c) => (
              <div key={c.title} className="rounded-xl border border-border bg-card p-6">
                <dt className="font-semibold text-foreground">{c.title}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.body}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="border-t border-border/60 py-10">
          <h2 className="text-2xl font-bold text-foreground">เปรียบเทียบทางเลือก</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[36rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-3 pr-4 font-semibold">ทางเลือก</th>
                  <th className="py-3 pr-4 font-semibold">เหมาะกับ</th>
                  <th className="py-3 font-semibold">จุดที่ต้องระวัง</th>
                </tr>
              </thead>
              <tbody>
                {s.compare.map((c) => (
                  <tr key={c.option} className="border-b border-border/60 align-top">
                    <td className="py-3 pr-4 font-medium text-foreground">{c.option}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{c.fit}</td>
                    <td className="py-3 text-muted-foreground">{c.watch}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="border-t border-border/60 py-10">
          <h2 className="text-2xl font-bold text-foreground">งานที่มักทำต่อเนื่องกัน</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {related.map((r) => (
              <li key={r.slug}>
                <Link
                  to="/virtual-office/$service"
                  params={{ service: r.slug }}
                  className="text-primary hover:underline"
                >
                  {r.nameTh}
                </Link>{" "}
                <span className="text-muted-foreground">— {r.tagline}</span>
              </li>
            ))}
            <li>
              <Link to="/accounting" className="text-primary hover:underline">
                งานบัญชีและภาษีต่อเนื่องหลังจดทะเบียน
              </Link>
            </li>
            <li>
              <Link to="/practice/$area" params={{ area: "corporate" }} className="text-primary hover:underline">
                สายงานบริษัทและสัญญาของสำนักงาน
              </Link>
            </li>
          </ul>
          <p className="mt-4 text-sm">
            <Link to="/virtual-office" className="text-primary hover:underline">
              ดูภาพรวมบริการที่ตั้งสำนักงานและการจด VAT ทั้งหมด →
            </Link>
          </p>
        </section>
      </div>

      <QaHubCta base="/virtual-office" label={s.nameTh} qa={qa} />
      <FirmCta
        heading={`ปรึกษาเรื่อง${s.nameTh}`}
        body="ส่งภาพเอกสารหรือเล่าลักษณะธุรกิจมาให้ทีมงานตรวจเบื้องต้น เราจะแจ้งเอกสารที่ต้องเตรียม ลำดับงาน และจุดที่มักถูกให้แก้ไข พร้อมแจ้งขอบเขตงานและค่าบริการเป็นลายลักษณ์อักษรก่อนเริ่มงาน"
      />
    </>
  );
}

export const VO_SERVICE_SLUGS = VO_SERVICES.map((s) => s.slug);
